import { USA_STATE_LIST } from "../constant/constant";
import { formatIsoDate, formatPhone } from "./cpFieldRules";
import { missingPolicy } from "./cpDraftFields";

const STATES = [...USA_STATE_LIST, "Test (TE)"];
const RECORD_KEYS = ["name", "email", "phone", "address", "license_number", "license_expiration", "dea_number", "specialty", "signing_date", "state_name"];
const text = (value) => typeof value === "string" ? value.trim() : "";
const date = (value) => /^\d{4}-\d{2}-\d{2}$/.test(text(value)) ? formatIsoDate(text(value)) : "";
const validState = (value) => Number.isInteger(value) && value >= 1 && value <= STATES.length;
const outcome = (status, value = "", editable = false) => ({ status, value, editable });

// Explicit CP draft sources are authoritative. Legacy name-based signing does not use this resolver.
// Each role's current licenses must include state (1-based); dates are normalized date-only ISO strings.
export function resolveCpDraftField(field, context, sources = []) {
  if (!["physician", "operator"].includes(field.signer_role)
    || !["text", "date", "checkbox", "signature"].includes(field.field_type)
    || typeof field.required !== "boolean"
    || !["record", "signer"].includes(field.value_source)
    || field.missing_policy !== missingPolicy(field, sources)) return outcome("invalid");

  if (field.value_source === "signer") {
    if (field.source_key !== null) return outcome("invalid");
    if (field.field_type === "signature") return outcome("signature");
    return outcome(field.required ? "ask_signer" : "leave_blank", "", true);
  }

  const source = sources.find((entry) => entry.key === field.source_key);
  if (!source || !RECORD_KEYS.includes(field.source_key) || !source.roles.includes(field.signer_role)
    || source.field_type !== field.field_type) return outcome("invalid");

  const record = context?.signers?.[field.signer_role];
  const isLicense = ["license_number", "license_expiration", "dea_number"].includes(field.source_key);
  if ((isLicense || field.source_key === "state_name") && !validState(context?.state_id)) return outcome("invalid");
  if (!["state_name", "signing_date"].includes(field.source_key) && !record) return outcome("unavailable");
  const availability = ["state_name", "signing_date"].includes(field.source_key) ? context?.source_availability : record?.source_availability;
  if (availability?.[field.source_key] === "unavailable") return outcome("unavailable");
  if (isLicense && !Array.isArray(record.licenses)) return outcome("unavailable");

  let value = "";
  if (isLicense) {
    const licenses = record.licenses.filter((license) => Number(license?.state) === context.state_id);
    if (field.source_key === "dea_number") {
      value = [...new Set(licenses.map((license) => text(license.dea_number)).filter(Boolean))].join(", ");
    } else {
      const expiryByNumber = new Map();
      for (const license of licenses) {
        const number = text(license.license_number);
        const expiry = date(license.expiration_date) ? text(license.expiration_date) : "";
        if (number && (!expiryByNumber.has(number) || expiry > expiryByNumber.get(number))) expiryByNumber.set(number, expiry);
      }
      value = field.source_key === "license_number"
        ? [...expiryByNumber.keys()].join(", ")
        : [...expiryByNumber.values()].every(Boolean) ? [...expiryByNumber.values()].map(date).join(", ") : "";
    }
  } else if (field.source_key === "state_name") {
    value = STATES[context.state_id - 1];
  } else if (field.source_key === "signing_date") {
    if (context?.signing_date === null && availability?.signing_date === "pending_signature") return outcome("pending_signature");
    value = date(context?.signing_date);
  } else {
    value = field.source_key === "phone" ? formatPhone(text(record.phone)) : text(record[field.source_key]);
    // A malformed phone cannot prefill the signing page's 10-digit input.
    if (field.source_key === "phone" && value.replace(/\D/g, "").length !== 10) value = "";
  }

  if (value) return outcome("prefilled", value, source.editable);
  return outcome(["hold_required", "block_document"].includes(field.missing_policy) ? "hold_document" : field.missing_policy, "", source.editable);
}

// Statuses where a record field stops the signer: signing pages always name these fields.
export const CP_HOLD_STATUSES = ["hold_document", "unavailable", "invalid"];

export const CP_PREVIEW_STATUS = {
  prefilled: "Will prefill",
  ask_signer: "Signer fills in",
  leave_blank: "Can stay blank",
  signature: "Signer signs here",
  pending_signature: "Filled in when signed",
  hold_document: "Missing record — document on hold",
  unavailable: "Record data unavailable — retry before signing",
  invalid: "Check field source and settings",
};

export const CP_PREVIEW_PLACEHOLDER = {
  ask_signer: "Signer fills in",
  leave_blank: "Optional",
  signature: "Sign here",
  pending_signature: "Filled in when signed",
  hold_document: "Record missing",
  unavailable: "Record unavailable",
  invalid: "Check source",
};

// These examples never fetch, persist or impersonate real signer records.
export function cpDraftSampleContext(stateId, missing = false) {
  const license = (role) => ({ state: stateId, license_number: `${role === "physician" ? "MD" : "NP"}-SAMPLE-${stateId}`, expiration_date: "2028-06-30", dea_number: role === "physician" ? "SAMPLE-DEAP" : "SAMPLE-DEAC" });
  return {
    state_id: stateId,
    signing_date: "2026-10-05",
    signers: {
      physician: { name: missing ? "" : "Dr. Alex Example", email: missing ? "" : "physician@example.test", phone: missing ? "" : "2025550100", specialty: missing ? "" : "Family medicine", licenses: missing ? [] : [license("physician")] },
      operator: { name: missing ? "" : "Jordan Example", email: missing ? "" : "clinician@example.test", phone: missing ? "" : "2025550101", address: missing ? "" : "123 Example Street\nSample City, ST 00000", specialty: missing ? "" : "Primary care", licenses: missing ? [] : [license("operator")] },
    },
  };
}
